import type { JSX } from 'react'

import { BoardColumn } from './BoardColumn'
import type { BoardColumnItem } from './BoardColumn'
import type { BoardColumnConfig } from './BoardColumnConfig'
import { useBoardColumnPosts } from './useBoardColumnPosts'
import { useBoardDraftsColumn } from './useBoardDraftsColumn'
import { useBoardTagContentsColumn } from './useBoardTagContentsColumn'
import type { BoardDateRange } from './useBoardState'

function sortKeyForPost(post: {
  dueAt: string | null
  sentAt: string | null
  createdAt: string
}): string {
  return post.dueAt ?? post.sentAt ?? post.createdAt
}

type BoardColumnContainerProps = {
  config: BoardColumnConfig
  dateRange: BoardDateRange | undefined
  /** Shared across every column — content items are one paginated list, bucketed. */
  contentItemsHasNextPage: boolean
  contentItemsLastElementRef: (node?: Element | null) => void
  /** Id of the last item in the underlying content-items connection, wherever it lands. */
  lastContentItemId: string | undefined
}

export function BoardColumnContainer(
  props: BoardColumnContainerProps,
): JSX.Element {
  if (props.config.useUnifiedDraftsQuery) {
    return <BoardDraftsColumnContainer config={props.config} />
  }
  if (props.config.useUnifiedContentsQuery) {
    return (
      <BoardContentsColumnContainer
        config={props.config}
        dateRange={props.dateRange}
      />
    )
  }
  return <BoardPostsColumnContainer {...props} />
}

function BoardPostsColumnContainer({
  config,
  dateRange,
  contentItemsHasNextPage,
  contentItemsLastElementRef,
  lastContentItemId,
}: BoardColumnContainerProps): JSX.Element {
  const {
    posts,
    loading,
    fetchingMore,
    reloading,
    error,
    lastElementRef,
    canShowMore,
    showMore,
    hasNextPage,
  } = useBoardColumnPosts({
    statuses: config.statuses,
    channelIds: config.channelIds,
    tagIds: config.tagIds,
    dateRangeOverride: dateRange,
    windowRule: config.windowRule,
    skip: config.postsDisabled,
  })

  const ascending = config.windowRule === 'upcoming-30-days'
  const items: BoardColumnItem[] = [
    ...posts.map(
      (post): BoardColumnItem => ({
        kind: 'post',
        id: post.id,
        post,
      }),
    ),
    ...config.contentItems.map(
      (item): BoardColumnItem => ({
        kind: 'contentItem',
        id: item.id,
        item,
      }),
    ),
  ].toSorted((a, b) => {
    const aKey =
      a.kind === 'post'
        ? sortKeyForPost(a.post)
        : (a.item.targetDate ?? a.item.createdAt)
    const bKey =
      b.kind === 'post'
        ? sortKeyForPost(b.post)
        : (b.item.targetDate ?? b.item.createdAt)
    return ascending ? aKey.localeCompare(bKey) : bKey.localeCompare(aKey)
  })

  const lastPost = posts.at(-1)
  // The globally-last content item lands in exactly one column's bucket (or
  // none, if a filter excludes it) — only that column wires up the shared
  // content-items pagination trigger.
  const rendersLastContentItem =
    lastContentItemId !== undefined &&
    config.contentItems.some((item) => item.id === lastContentItemId)

  // The header count reflects only what's loaded so far, not the column's
  // real total — mark it approximate whenever either underlying connection
  // (posts, or content items if this column renders their pagination
  // trigger) still has more pages to fetch.
  const countIsApproximate =
    hasNextPage || (rendersLastContentItem && contentItemsHasNextPage)

  // canShowMore only ever renders for these two window rules — 'none' means
  // no windowing exists in the first place, so nothing to widen.
  const showMoreLabel =
    config.windowRule === 'past-30-days'
      ? 'Older posts hidden'
      : 'Posts scheduled further out are hidden'

  return (
    <BoardColumn
      label={config.label}
      count={items.length}
      countIsApproximate={countIsApproximate}
      tagColor={config.tagColor}
      channelAvatar={config.channelAvatar}
      items={items}
      loading={loading}
      fetchingMore={fetchingMore}
      reloading={reloading}
      error={error}
      lastPostId={lastPost?.id}
      lastElementRef={lastElementRef}
      lastContentItemId={
        rendersLastContentItem && contentItemsHasNextPage
          ? lastContentItemId
          : undefined
      }
      contentItemLastElementRef={contentItemsLastElementRef}
      canShowMore={canShowMore}
      showMoreLabel={showMoreLabel}
      onShowMore={showMore}
    />
  )
}

const noop = (): void => {}

/**
 * Draft is the one Status column that mixes posts and content items, so it
 * fetches both together through the unified `drafts` query instead of the
 * two-connection merge every other column uses — see `useBoardDraftsColumn`.
 * Never windowed (`windowRule: 'none'`), so there's no "Show more" to wire up.
 */
function BoardDraftsColumnContainer({
  config,
}: {
  config: BoardColumnConfig
}): JSX.Element {
  const {
    items,
    loading,
    fetchingMore,
    reloading,
    error,
    hasNextPage,
    lastElementRef,
  } = useBoardDraftsColumn({
    channelIds: config.channelIds,
    includeChannellessDrafts: config.includeChannellessDrafts,
    tagIds: config.tagIds,
  })

  const lastItem = items.at(-1)

  return (
    <BoardColumn
      label={config.label}
      count={items.length}
      countIsApproximate={hasNextPage}
      tagColor={config.tagColor}
      channelAvatar={config.channelAvatar}
      items={items}
      loading={loading}
      fetchingMore={fetchingMore}
      reloading={reloading}
      error={error}
      lastPostId={lastItem?.kind === 'post' ? lastItem.id : undefined}
      lastElementRef={lastElementRef}
      lastContentItemId={
        lastItem?.kind === 'contentItem' ? lastItem.id : undefined
      }
      contentItemLastElementRef={lastElementRef}
      canShowMore={false}
      showMoreLabel=""
      onShowMore={noop}
    />
  )
}

/**
 * A Tag-grouped column on the `contents` query, behind the
 * `board-view-contents-query` flag. It shows the server's `totalCount`, not
 * the approximate "loaded so far" count. It is never windowed, so it has no
 * "Show more".
 */
function BoardContentsColumnContainer({
  config,
  dateRange,
}: {
  config: BoardColumnConfig
  dateRange: BoardDateRange | undefined
}): JSX.Element {
  const {
    items,
    loading,
    fetchingMore,
    reloading,
    error,
    totalCount,
    countIsApproximate,
    lastElementRef,
  } = useBoardTagContentsColumn({
    channelIds: config.channelIds,
    tagIds: config.tagIds,
    statuses: config.statuses,
    dateRange,
  })

  const lastItem = items.at(-1)

  return (
    <BoardColumn
      label={config.label}
      count={totalCount}
      countIsApproximate={countIsApproximate}
      tagColor={config.tagColor}
      channelAvatar={config.channelAvatar}
      items={items}
      loading={loading}
      fetchingMore={fetchingMore}
      reloading={reloading}
      error={error}
      lastPostId={lastItem?.kind === 'post' ? lastItem.id : undefined}
      lastElementRef={lastElementRef}
      lastContentItemId={
        lastItem?.kind === 'contentItem' ? lastItem.id : undefined
      }
      contentItemLastElementRef={lastElementRef}
      canShowMore={false}
      showMoreLabel=""
      onShowMore={noop}
    />
  )
}
